import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import qs from 'qs';

import { IUploadedMaterial } from 'api/material/models';
import { mapFeedbackParamsToDto } from 'api/orders/helpers';
import { IFeedbackParams } from 'api/orders/models';
import { ordersQueryKey } from 'api/orders/query';
import { datahubAxiosClient, ENDPOINT_DETAILS } from 'constants/server/apiEndPoint';
import { LocalizedError, useLocalization } from 'localization';
import notifyHandler from 'utils/notifyHandler';
import { IDatahubTaskDto } from './dtos';
import { mapDatahubTaskFromDto, mapVirtualStagingFromDto } from './helpers';
import QUERIES from 'modules/common/queries';
import { CountStatus } from 'api/cases/query';
import { groupTextsFromDto } from 'api/texts/helpers';
import { ICaseTextGroup } from 'api/texts/models';
import { IDatahubTask, ITaskInputText, IVirtualStaging } from './models';
import { STATUS_LABEL } from 'constants/enums/status';
import { TasksApi } from 'api';

type CommonTaskParams = Partial<{
  taskId: string;
  type: string;
  email: string;
  name: string;
  addedByEmail: string;
  note: string;
  copyWriteTexts: MutatedCopyWriteText[];
  fileName: string;
  id: string;
  orderlineId: string;
  url: string;
  assetIds: string[];
  materialIds: string[];
  beforeMaterialId: string;
  afterMaterialId: string;
  roomType: string;
  batchId: string;
  code: string;
  furniture: string;
  label: string;
}>;

type Files = Pick<CommonTaskParams, 'fileName' | 'id' | 'orderlineId' | 'url'>;

type SealedFiles = {
  uploadedFiles: Pick<CommonTaskParams, 'name' | 'url'>[];
};

type UploadFileParams = Omit<Files, 'id'>;

type SectionTextResponses = { sectionName: string; content: string };

type MutatedCopyWriteText = {
  approved: boolean;
  copywriteTextType: string;
  description: string;
  editedText: string | null;
  originalText: string;
  editIsOpen: boolean;
};

export const taskQueryKeys = {
  all: ['tasks'] as const,
  getFiles: (orderId: string) => [...taskQueryKeys.all, orderId, 'materials'] as const,
  getTextSection: (orderId: string) => [...taskQueryKeys.all, orderId, 'textSection'] as const,
  openTasks: (caseId: string) => [...taskQueryKeys.all, caseId, 'openTasks'] as const,
  countTasks: () => [...taskQueryKeys.all, 'countTasks'] as const,
  virtualStaging: (type: string) => [...taskQueryKeys.all, 'virtualStaging', type] as const,
  getAITextEditorLink: (taskId: string) => [...taskQueryKeys.all, 'textEditorLink', taskId] as const,
};

export const sendEmailForTextTaskMutation = () => {
  const { t } = useLocalization();
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (params: CommonTaskParams) => {
      const { email, name, taskId = '' } = params;
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.sendEmailForInterviewTaskText(taskId), {
        email,
        name,
      });
      if (!response?.data?.success) {
        throw new Error('modals.basic.failed');
      }
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.basic.failed') });
    },
    onSuccess: () => {},
  });

  return { mutateAsync, isLoading, data, isSuccess };
};

export const addNoteForTextTaskMutation = () => {
  const { t } = useLocalization();
  return useMutation({
    mutationFn: async (params: CommonTaskParams) => {
      const { taskId = '', addedByEmail, note } = params;
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.addNoteForTaskText(taskId), {
        addedByEmail,
        note,
      });
      if (!response?.data?.success) throw new Error('Error with adding note');
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.copywriteText.submitFailed') });
    },
    onSuccess: () => {},
  });
};

export const sendQuestionnaireAITextTaskMutation = () => {
  const { t } = useLocalization();
  return useMutation({
    mutationFn: async (params: ITaskInputText) => {
      const { taskId = '', ...rest } = params;
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.sendQuestionnaireAITextEmail(taskId), rest);
      if (!response?.data?.success) throw new Error(t('modals.aiTextDetails.failed'));
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.aiTextDetails.failed') });
    },
    onSuccess: () => {},
  });
};

export const completeTaskMutation = () => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (taskId: string) => {
      const response = await datahubAxiosClient.patch(ENDPOINT_DETAILS.completeTask(taskId));
      if (!response?.data?.success) throw new Error('Error with completing task');
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.copywriteText.completeFailed') });
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.success') });
      queryClient.invalidateQueries({ queryKey: ordersQueryKey.orderTasks() });
      queryClient.invalidateQueries({ queryKey: [QUERIES.tasks] });
    },
  });
  return { mutateAsync, isLoading, data, isSuccess };
};

export const taskSomeSlideShowMutation = () => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (params: CommonTaskParams) => {
      const { taskId = '', type } = params;
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.completeSomeSlideShowTask(taskId), { type });
      if (!response?.data?.success) throw new Error('Error with social media slideshow');
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.socialMediaSlideshow.error') });
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.success') });
      queryClient.invalidateQueries({ queryKey: ordersQueryKey.orderTasks() });
      queryClient.invalidateQueries({ queryKey: [QUERIES.tasks] });
    },
  });
  return { mutateAsync, isLoading, data, isSuccess };
};

export const getCurrentTaskFilesApi = (orderId: string) => {
  return useQuery<IUploadedMaterial[]>({
    queryKey: taskQueryKeys.getFiles(orderId),
    queryFn: async ({ signal }) => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getUploadedFiles(orderId ?? ''), {
        signal: signal,
      });

      return response?.data ?? [];
    },
    enabled: !!orderId,
  });
};

export function getAITextEditorLink(taskId: IDatahubTask['id']) {
  return useQuery({
    queryKey: taskQueryKeys.getAITextEditorLink(taskId),
    queryFn: async () => (taskId ? await TasksApi.getAITextEditorLink(taskId) : undefined),
    enabled: !!taskId,
  });
}

export const removeUploadedMaterialMutation = (orderLineId: string) => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading, isSuccess, data } = useMutation<
    void, //the return type of the mutationFn
    LocalizedError,
    string, //fileId
    { previousFiles?: Files[] } // context to rollback
  >({
    mutationFn: async fileId => {
      await datahubAxiosClient.delete(ENDPOINT_DETAILS.removeUploadedFile(fileId));
    },
    onMutate: async fileId => {
      await queryClient.cancelQueries({ queryKey: taskQueryKeys.getFiles(orderLineId) });

      const previousFiles = queryClient.getQueryData<Files[]>(taskQueryKeys.getFiles(orderLineId));

      queryClient.setQueryData<Files[]>(
        taskQueryKeys.getFiles(orderLineId),
        old => old?.filter(f => f.id !== fileId) ?? []
      );

      return { previousFiles };
    },

    onError: (_error, _fileId, context) => {
      notifyHandler({ type: 'error', content: t('materials.messages.linkRemoveFailed') });
      if (context?.previousFiles) {
        queryClient.setQueryData(taskQueryKeys.getFiles(orderLineId), context.previousFiles);
      }
    },

    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.success') });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.getFiles(orderLineId) });
    },
  });

  return {
    mutateAsync,
    isLoading,
    isSuccess,
    data,
  };
};

export const registerBatchFilesMutation = (orderLineId: string) => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: UploadFileParams) => {
      const { fileName, orderlineId = '', url } = params;
      const response = datahubAxiosClient.post(ENDPOINT_DETAILS.upload(orderlineId), { fileName, url });
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.uploader.error') });
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.success') });
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.getFiles(orderLineId) });
    },
  });
};

export const getTextSectionByOrderIdApi = (orderId: string) => {
  const { data, isLoading, isFetching, isError, refetch } = useQuery<SectionTextResponses[]>({
    queryKey: taskQueryKeys.getTextSection(orderId),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getTextSection(orderId ?? ''));
      return response?.data ?? [];
    },
    enabled: !!orderId,
    staleTime: 0,
  });
  return { data, isLoading, isFetching, isError, refetch };
};

export const saveCopyWriteTextMutation = (orderTaskId: string) => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (params: CommonTaskParams) => {
      const { copyWriteTexts } = params;
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.saveCopyWriteTexts(), {
        copyWriteTexts,
        orderTaskId,
      });
      if (!response?.data) throw new Error('Error with saving copy write text');
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.copywriteText.submitFailed') });
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.success') });
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.getTextSection(orderTaskId) });
    },
  });
  return { mutateAsync, isLoading, data, isSuccess };
};

export const submitFeedBackMutation = () => {
  const { t } = useLocalization();
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (params: IFeedbackParams) => {
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.submitFeedback(), mapFeedbackParamsToDto(params));
      if (!response?.data?.success) throw new Error('Error with submiting feedback');
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.uploader.failed') });
    },
    onSuccess: () => {},
  });
  return { mutateAsync, isLoading, data, isSuccess };
};

export const getOpenTasksByCaseIdAPi = (caseId: string) => {
  const { data, isLoading, isFetching, isError, refetch } = useQuery<IDatahubTaskDto[]>({
    queryKey: taskQueryKeys.openTasks(caseId),
    queryFn: async ({ signal }) => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.openTasksByCaseId(caseId), {
        signal,
      });
      return response?.data?.map(mapDatahubTaskFromDto) || [];
    },
    enabled: !!caseId,
  });
  return { data, isLoading, isFetching, isError, refetch };
};

export const sealUploadedFilesMutation = (orderLineId: string) => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (payload: SealedFiles) => {
      const { uploadedFiles } = payload;
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.sealUploadedFiles(orderLineId), uploadedFiles);
      if (!response?.data?.success) throw new Error('Error with sealing files');
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.uploader.error') });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.getFiles(orderLineId) });
    },
  });
  return { mutateAsync, isLoading, data, isSuccess };
};

export const countTasksApi = (caseId?: string, queryOptions?: Omit<UseQueryOptions<CountStatus>, 'queryKey' | 'queryFn'>) => {
  const { data, isLoading, isFetching, isError, isSuccess, refetch } = useQuery<CountStatus>({
    queryKey: taskQueryKeys.countTasks(),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getNumberOfTasks(), {
        params: {
          statuses: [STATUS_LABEL.open, STATUS_LABEL.partial, STATUS_LABEL.completed, STATUS_LABEL.cancelled],
          caseId: caseId,
        },
        paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
      });
      return response.data;
    },
    staleTime: 60000,
    ...queryOptions,
  });

  return { data, isLoading, isFetching, isError, isSuccess, refetch };
};

export const completeImagesSelectorMutation = (taskId: string, apiType: string) => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (assets: { assetId: number; url?: string }[] | number[]) => {
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.completeMultipleTasks(taskId, apiType), {
        materials: assets,
      });
      if (!response?.data?.success) throw new Error('Error sending image selectors');
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.whiteboxImageSelector.error') });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERIES.tasks] });
      queryClient.invalidateQueries({ queryKey: [QUERIES.tasks, category] });
      notifyHandler({ type: 'success', content: t('modals.whiteboxImageSelector.success') });
    },
  });
  return { mutateAsync, isLoading, data, isSuccess };
};

export const beforeAfterImagesMutation = (taskId: string, apiType: string) => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (params: CommonTaskParams) => {
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.completeMultipleTasks(taskId, apiType), {
        ...params,
      });
      if (!response?.data?.success) throw new Error('Error with before and after trigger');
      return response;
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('modals.beforeAfter.success') });
      queryClient.invalidateQueries({ queryKey: [QUERIES.tasks] });
      queryClient.invalidateQueries({ queryKey: [QUERIES.tasks, category] });
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.beforeAfter.failed') });
    },
  });
  return { mutateAsync, isLoading, data, isSuccess };
};

export const inputSpeakSlideShowApi = (caseId: string) => {
  const { data, isLoading, isFetching, isError, refetch } = useQuery<ICaseTextGroup[]>({
    queryKey: taskQueryKeys.openTasks(caseId),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getInputTextSlideShow(), {
        params: { caseId },
      });
      return response?.data ? groupTextsFromDto(response.data) : [];
    },
    enabled: !!caseId,
  });
  return { data, isLoading, isFetching, isError, refetch };
};

export const virtualStagingApi = (type: string, isTriggered: boolean) => {
  const { data, isLoading, isFetching, isError } = useQuery<IVirtualStaging[]>({
    queryKey: taskQueryKeys.virtualStaging(type),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.virtualStaging(), {
        params: { type },
      });

      return (response?.data || []).map(mapVirtualStagingFromDto);
    },
    enabled: !!type && isTriggered,
  });

  return { data, isLoading, isFetching, isError };
};

export const virtualStagingMutation = (taskId: string) => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (params: CommonTaskParams) => {
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.completeVirtualStaging(taskId), {
        ...params,
      });
      if (!response?.data?.success) throw new Error('Error with virtual staging');
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.beforeAfter.failed') });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERIES.tasks] });
      queryClient.invalidateQueries({ queryKey: [QUERIES.tasks, category] });
      notifyHandler({ type: 'success', content: t('modals.beforeAfter.success') });
    },
  });
  return { mutateAsync, isLoading, data, isSuccess };
};
